import ErrorIcon from "@mui/icons-material/Error"
import HelpCenterIcon from "@mui/icons-material/HelpCenter"
import HourglassBottomRoundedIcon from "@mui/icons-material/HourglassBottomRounded"
import LinearScaleIcon from "@mui/icons-material/LinearScale"
import PsychologyIcon from "@mui/icons-material/Psychology"
import SendIcon from "@mui/icons-material/Send"
import ThumbUpIcon from "@mui/icons-material/ThumbUp"
import UpdateIcon from "@mui/icons-material/Update"
import { AlertColor, SvgIconProps } from "@mui/material"
import { ReactElement } from "react"

import { InstallerApplicationStatuses } from "../../constants/enums"
import { UNKNOWN_STATUS } from "../../constants/generalConstants"
import { SUPPORT_REFER_ENTITY } from "../../errors/errorMessages"

type StatusDispatcherType = {
    [key: string]: {
        status: InstallerApplicationStatuses | string
        statusMessage: string | string[]
        statusIcon: ReactElement<SvgIconProps>
        alertSeverity: AlertColor
    }
}

export const STATUS_DISPATCHER = (dno: string): StatusDispatcherType => ({
    [InstallerApplicationStatuses.autoApproved]: {
        status: InstallerApplicationStatuses.autoApproved,
        statusMessage: `Great news! This application has been auto-approved. You can proceed with the installation. ${
            dno ? `${dno} ` : ""
        }has been notified of this outcome`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "success",
    },
    [InstallerApplicationStatuses.dnoApproved]: {
        status: InstallerApplicationStatuses.dnoApproved,
        statusMessage: `Great news! This application has been approved ${
            dno ? `by ${dno} ` : ""
        }. You are all set to proceed with the installation`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "success",
    },
    [InstallerApplicationStatuses.dnoApprovedWithLimit]: {
        status: InstallerApplicationStatuses.dnoApprovedWithLimit,
        statusMessage: `Good news! This application has been approved by ${
            dno ? `${dno}` : ""
        } with some conditions. You are all set to proceed with the installation. See below for all the details`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "success",
    },
    [InstallerApplicationStatuses.dnoFurtherInfoRequired]: {
        status: "Installer Information Required",
        statusMessage: `${
            dno ? `${dno} ` : ""
        }requires more information from the installer in order to make a decision on this application. It will reach out to the customer to organise next steps`,
        statusIcon: <HelpCenterIcon fontSize="large" />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.dnoWorksRequired]: {
        status: InstallerApplicationStatuses.dnoWorksRequired,
        statusMessage: `${
            dno ? `${dno} ` : ""
        }has reviewed your application and require works to be carried out by the DNO before this device can be connected.`,
        statusIcon: <HelpCenterIcon fontSize="large" />,
        alertSeverity: "warning",
    },
    [InstallerApplicationStatuses.awaitingAssessment]: {
        status: InstallerApplicationStatuses.awaitingAssessment,
        statusMessage: `We are in the process of assessing this application for you! An outcome will be reported here very shortly. If the application passes the assessment, it will be approved. If not, we will escalate it to ${
            dno ? `${dno} for you ` : ""
        } for you. No further action is required at this stage`,
        statusIcon: <LinearScaleIcon />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.underAssessment]: {
        status: InstallerApplicationStatuses.underAssessment,
        statusMessage: `We are in the process of assessing this application for you! An outcome will be reported here very shortly. If the application passes the assessment, it will be approved. If not, we will escalate it to ${
            dno ? `${dno} for you ` : ""
        } for you. No further action is required at this stage`,
        statusIcon: <LinearScaleIcon />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.escalatedToDno]: {
        status: InstallerApplicationStatuses.escalatedToDno,
        statusMessage: `We have escalated this application to ${
            dno ? `${dno} for you` : ""
        }. They will look into this application and get back to you shortly. Any updates will be communicated to you via this webpage. No further action is required at this stage`,
        statusIcon: <SendIcon fontSize="large" />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.failedToLarge]: {
        status: InstallerApplicationStatuses.failedToLarge,
        statusMessage: [
            `This application has failed as it exceeds the maximum application size permitted by Connect Direct.${dno} has not been notified of this application.The application will be closed and no further action will be taken on it.`,
            `For generation applications: Please complete the relevant G99 generation form and contact ${dno} to get connected.`,
            `For demand applications: Please reach out to ${dno} to get connected.`,
        ],
        statusIcon: <ErrorIcon />,
        alertSeverity: "error",
    },
    [InstallerApplicationStatuses.failed]: {
        status: InstallerApplicationStatuses.failed,
        statusMessage: `Something went wrong whilst processing this application. Please report this to ${SUPPORT_REFER_ENTITY}. ${dno} has not been notified of this application. You can amend this application by adding a different image and retry the assessment.`,
        statusIcon: <ErrorIcon />,
        alertSeverity: "error",
    },
    [InstallerApplicationStatuses.imageScanning]: {
        status: InstallerApplicationStatuses.imageScanning,
        statusMessage: `We are currently scanning the cut-out image for this application as part of our automated checks. No further action is required at this stage. Check back here shortly for an update!`,
        statusIcon: <PsychologyIcon />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.imageScanFailed]: {
        status: InstallerApplicationStatuses.imageScanFailed,
        statusMessage: `Something went wrong whilst scanning the cut-out image for this application. Please report this to ${SUPPORT_REFER_ENTITY}. ${dno} has not been notified of this application. You can amend this application by adding a different image and retry the assessment.`,
        statusIcon: <ErrorIcon />,
        alertSeverity: "error",
    },
    [UNKNOWN_STATUS]: {
        status: UNKNOWN_STATUS,
        statusMessage: "Please see below for the latest on your application!",
        statusIcon: <ErrorIcon />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.invalidCutOut]: {
        status: InstallerApplicationStatuses.invalidCutOut,
        statusMessage:
            "The cut-out image you provided was assessed as invalid. This application will not be progressed further. You can amend this application by adding a different image and retry the assessment. Please ensure the image is clear and taken from within 1 meter of the cut-out",
        statusIcon: <ErrorIcon />,
        alertSeverity: "error",
    },
    [InstallerApplicationStatuses.cxSubmitted]: {
        status: InstallerApplicationStatuses.cxSubmitted,
        statusMessage: `Commissioning details for this application have been submitted and are currently being reviewed by ${dno}. This is not the final step - once reviewed and approved, you could receive the Export MPAN which marks the completion of the process. No further action is required at this stage. Check back here shortly for an update!`,
        statusIcon: <HourglassBottomRoundedIcon />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.cxUpdateRequired]: {
        status: InstallerApplicationStatuses.cxUpdateRequired,
        statusMessage: `The commissioning information for this application has been by reviewed the team at ${dno} and they have requested an update. Please review the comments in the action histroy below and once ready, re-open to commissioning form to resubmit your information. We will pre-populate it with the current information to make this process as quick as possible for you!`,
        statusIcon: <UpdateIcon />,
        alertSeverity: "warning",
    },
    [InstallerApplicationStatuses.cxAccepted]: {
        status: InstallerApplicationStatuses.cxAccepted,
        statusMessage: `Commissioning details for this application have been reviewed by ${dno} and approved! The process is complete and no further action is required!`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "success",
    },
    [InstallerApplicationStatuses.reAutoApproved]: {
        status: InstallerApplicationStatuses.reAutoApproved,
        statusMessage: `Great news! Your amendment to this application has been auto-approved. ${
            dno ? `${dno} ` : ""
        }has been notified of this outcome`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "success",
    },
    [InstallerApplicationStatuses.reEscalatedToDno]: {
        status: InstallerApplicationStatuses.reEscalatedToDno,
        statusMessage: `We have escalated your amended application to ${
            dno ? `${dno} for you` : ""
        }. They will look into this application and get back to you shortly. Any updates will be communicated to you via this webpage. No further action is required at this stage`,
        statusIcon: <SendIcon fontSize="large" />,
        alertSeverity: "info",
    },
    [InstallerApplicationStatuses.exMpanProvided]: {
        status: InstallerApplicationStatuses.exMpanProvided,
        statusMessage: `Export MPAN has been provided for this application. This is the final step in the process within connect direct.`,
        statusIcon: <ThumbUpIcon fontSize="large" />,
        alertSeverity: "info",
    },
})
